import { useState } from 'react';
import axios from 'axios';
import { useAuth, API } from '../../App';
import { toast } from 'sonner';
import { Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function DriverRidesExportDialog({
  open,
  onOpenChange,
  driverId,
  driverName,
  defaultYear,
  defaultMonth,
}) {
  const { token } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(defaultYear ?? now.getFullYear());
  const [month, setMonth] = useState(defaultMonth ?? (now.getMonth() + 1));
  const [downloading, setDownloading] = useState(null); // 'xlsx' | 'pdf' | null

  const currY = now.getFullYear();
  const years = [];
  for (let y = currY - 3; y <= currY + 1; y++) years.push(y);

  const download = async (format) => {
    if (!driverId) return;
    setDownloading(format);
    try {
      const monthStr = `${year}-${String(month).padStart(2, '0')}`;
      const res = await axios.get(
        `${API}/reports/driver/${driverId}?month=${monthStr}&format=${format}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );

      // Try to read filename from Content-Disposition
      let filename = `Rides_${(driverName || 'driver').replace(/\s+/g, '_')}_${MONTHS[month - 1]}_${year}.${format}`;
      const cd = res.headers['content-disposition'] || res.headers['Content-Disposition'];
      if (cd) {
        const m = /filename="?([^"]+)"?/.exec(cd);
        if (m && m[1]) filename = m[1];
      }

      const blob = new Blob([res.data], {
        type: format === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      console.error('Export driver rides error:', e);
      toast.error(e.response?.data?.detail || 'Failed to download report');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="driver-rides-export-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5 text-amber-600" />
            Export rides — {driverName || 'Driver'}
          </DialogTitle>
          <DialogDescription>
            Pick a month, then choose Excel or PDF. All rides except free-cancelled are included.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 pt-2">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Month</label>
            <select
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value, 10))}
              className="w-full border border-slate-300 rounded h-10 text-sm px-2 bg-white"
              data-testid="driver-export-month-select"
            >
              {MONTHS.map((m, idx) => (
                <option key={m} value={idx + 1}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Year</label>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
              className="w-full border border-slate-300 rounded h-10 text-sm px-2 bg-white"
              data-testid="driver-export-year-select"
            >
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 pt-4">
          <Button
            onClick={() => download('xlsx')}
            disabled={downloading !== null}
            className="bg-emerald-600 hover:bg-emerald-700 text-white h-11"
            data-testid="driver-export-xlsx-btn"
          >
            {downloading === 'xlsx' ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <FileSpreadsheet className="h-4 w-4 mr-2" />
            )}
            Excel (.xlsx)
          </Button>
          <Button
            onClick={() => download('pdf')}
            disabled={downloading !== null}
            className="bg-rose-600 hover:bg-rose-700 text-white h-11"
            data-testid="driver-export-pdf-btn"
          >
            {downloading === 'pdf' ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <FileText className="h-4 w-4 mr-2" />
            )}
            PDF
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
